import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useResizeObserver from 'hooks/useResizeObserver'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LuxPlusPromoCard from './LuxPlusPromoCard'
import type { LandingPagePromotion } from 'luxPlus/constants/promotions'

const CARD_HORIZONTAL_MARGIN = 20
const SLIDE_DURATION_MS = 5000

// Styled Components
const CarouselWrapper = styled.div`
  position: relative;
  max-width: ${rem(400)};
  flex-shrink: 1;
  min-width: 0;

  // Readjust arrow positions due to card margin 
  button[data-direction="left"] {
    left: ${rem(CARD_HORIZONTAL_MARGIN)};
  }

  button[data-direction="right"] {
    right: ${rem(CARD_HORIZONTAL_MARGIN)};
  }
`

// Types
interface Props {
  promotions: Array<LandingPagePromotion>
}

// Components
function LuxPlusPromoCardCarousel({ promotions }: Props) {
  // Refs to change card heights
  const carouselRef = useRef<HTMLDivElement>(null)
  const innerCardRefs = useRef<Array<HTMLDivElement | null>>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [cardHeight, setCardHeight] = useState(0)

  const showPips = promotions.length > 1
  const [autoPlayPaused, setAutoPlayPaused] = useState(false)
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  const remainingRef = useRef(SLIDE_DURATION_MS)
  const lastResumeRef = useRef(Date.now())

  // Reset card height on resize to recalculate
  useResizeObserver(carouselRef, () => {
    setCardHeight(0)
  })

  // Calculate height of tallest card for consistent sizing
  useLayoutEffect(() => {
    if (cardHeight !== 0) return

    const heights = innerCardRefs.current
      .filter((ref): ref is HTMLDivElement => ref !== null)
      .map(ref => ref.offsetHeight)

    if (heights.length === 0) return

    const maxHeight = Math.max(...heights)
    setCardHeight(maxHeight)
  }, [promotions.length, cardHeight])

  // Track scroll position for pagination pips
  const handleCarouselScroll = useCallback(() => {
    if (!carouselRef.current) return

    const { scrollLeft, scrollWidth } = carouselRef.current
    const cardWidth = scrollWidth / promotions.length
    const newIndex = Math.round(scrollLeft / cardWidth)

    setActiveIndex(Math.min(Math.max(newIndex, 0), promotions.length - 1))
  }, [promotions.length])

  // Scroll to a given carousel indicator index
  const handleCarouselIndicatorClick = useCallback((index: number) => {
    if (!carouselRef.current) return

    const itemWidth = carouselRef.current.scrollWidth / promotions.length
    carouselRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth',
    })
  }, [promotions.length])

  useEffect(() => {
    remainingRef.current = SLIDE_DURATION_MS
  }, [activeIndex])

  useEffect(() => {
    if (autoPlayPaused || promotions.length <= 1) return

    lastResumeRef.current = Date.now()

    const timerId = setTimeout(() => {
      const next = (activeIndexRef.current + 1) % promotions.length
      handleCarouselIndicatorClick(next)
    }, remainingRef.current)

    return () => {
      clearTimeout(timerId)
      const elapsed = Date.now() - lastResumeRef.current
      remainingRef.current = Math.max(0, remainingRef.current - elapsed)
    }
  }, [activeIndex, autoPlayPaused, promotions.length, handleCarouselIndicatorClick])

  const handleHoverChange = useCallback((hovered: boolean) => {
    setAutoPlayPaused(hovered)
  }, [])

  if (promotions.length === 0) {
    return null
  }

  return (
    <CarouselWrapper>
      <CardCarousel
        ref={carouselRef}
        pageSize={1}
        gap={0}
        width="100%"
        snap="start"
        onScroll={handleCarouselScroll}
        className={getThemeClassName({ mobile: 'default' })}
      >
        {promotions.map((promotion, index) => (
          <LuxPlusPromoCard
            promotion={promotion}
            key={index}
            minHeight={cardHeight || undefined}
            cardRef={(el) => {
              innerCardRefs.current[index] = el
            }}
            carousel={{
              horizontalMargin: CARD_HORIZONTAL_MARGIN,
              activeIndex,
              count: promotions.length,
              onPipClick: handleCarouselIndicatorClick,
              onHoverChange: handleHoverChange,
              showPips,
              slideDuration: SLIDE_DURATION_MS,
              paused: autoPlayPaused,
            }}
          />
        ))}
      </CardCarousel>
    </CarouselWrapper>
  )
}

export default LuxPlusPromoCardCarousel
